<template>
  <div>
    <!-- Header with logo and account name - Dashboard Style with Transparent Background -->
    <div class="fixed top-0 left-0 right-0 z-20 px-2 sm:px-4 py-2 sm:py-3">
      <div class="max-w-7xl mx-auto">
        <div class="rounded-full px-3 sm:px-6 py-2 sm:py-3 flex justify-between items-center bg-black/60 backdrop-blur-lg shadow-md border border-white/20">
          <!-- Left: Engagr Logo and Dealer Name -->
          <div class="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <img :src="logoURLWH" alt="Engagr" class="h-6 sm:h-8 flex-shrink-0" />
            <div class="h-4 sm:h-6 w-px bg-white/30"></div>
            <span class="text-white font-medium text-xs sm:text-sm md:text-base truncate">
              {{ dealerName }}
            </span>
          </div>
          
          <!-- Right: Navigation Buttons -->
          <div class="flex items-center space-x-1.5 sm:space-x-3">
            <!-- Dashboard Button -->
            <router-link :to="{ name: 'dashboard', params: { slug: routeSlug }}" 
              class="inline-flex items-center justify-center w-12 h-12 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 bg-blue-600 hover:bg-blue-700 rounded-full transition-all duration-300 shadow-sm text-white text-xs sm:text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span class="hidden sm:inline">Dashboard</span>
            </router-link>
            
            <!-- Communication Tracking Button -->
            <router-link :to="{ name: 'communication-tracking', params: { slug: routeSlug }}" 
              class="inline-flex items-center justify-center w-12 h-12 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 bg-cyan-600 hover:bg-cyan-700 rounded-full transition-all duration-300 shadow-sm text-white text-xs sm:text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span class="hidden sm:inline">Tracking</span>
            </router-link>
            
            <!-- Templates Button -->
            <router-link :to="{ name: 'notifications', params: { slug: routeSlug }}" 
              class="inline-flex items-center justify-center w-12 h-12 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 bg-blue-600 hover:bg-blue-700 rounded-full transition-all duration-300 shadow-sm text-white text-xs sm:text-sm font-medium">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span class="hidden sm:inline">Templates</span>
            </router-link>
            
            <!-- Camera Button -->
            <router-link :to="'/account/' + routeSlug" 
              class="relative w-12 h-12 sm:w-10 sm:h-10 bg-white hover:bg-gray-100 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm">
              <!-- Pulse Animation Behind Button -->
              <span class="absolute inset-0 rounded-full animate-ping bg-blue-400 opacity-30"></span>
              <!-- Camera Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="relative z-10 w-5 h-5 sm:w-5 sm:h-5 text-blue-600">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Spacer to prevent content from being hidden under the navbar -->
    <div class="h-16 sm:h-20"></div>
  </div>
</template>

<script>
export default {
  name: 'NavHeader',
  props: {
    dealerName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      logoURLWH: '/assets/engagr-wh.svg'
    };
  },
  computed: {
    routeSlug() {
      return this.$route.params.slug;
    }
  }
};
</script>

<style scoped>
/* Modern header styles */
.bg-gray-900 {
  background-color: #1a202c;
}

.fixed {
  position: fixed;
}
</style>
